import {useEffect, useRef, useState} from 'react';
import {Button, Grid, TextField, Typography, Snackbar} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {useTheme} from '@material-ui/core/styles';
import QRCode from 'qrcode.react';
import Facebook from 'assets/social/facebook.svg';
import LinkedIn from 'assets/social/linkedin.svg';
import GMail from 'assets/social/gmail.svg';
import Copy from 'assets/social/copy.svg';
import {useGetPrices, useGetReferrals} from 'hooks/useGetEndpoint';

const SocialButton = ({
    icon,
    alt,
    url,
    onClick,
    children,
    wide,
    small,
    style,
}) => {
    const iconImg = (
        <img
            src={icon}
            alt=''
            style={small ? {height: '1rem'} : {width: '100%'}}
        />
    );

    return url ? (
        <a
            href={url}
            title={alt}
            onClick={onClick}
            target='_blank'
            rel='noreferrer'
            style={style}
        >
            {wide ? (
                <Grid container spacing={2} alignItems='center'>
                    <Grid xs={6} item>
                        {iconImg}
                    </Grid>
                    <Grid xs={6} item>
                        {children}
                    </Grid>
                </Grid>
            ) : (
                [iconImg, children]
            )}
        </a>
    ) : (
        <Button
            variant='contained'
            title={alt}
            onClick={onClick}
            startIcon={iconImg}
        >
            {children}
        </Button>
    );
};

const emailSubjectTemplate = encodeURIComponent(
    'I got a referral code so you can try MM-SEAS'
);

const ReferralWidget = ({code}) => {
    const theme = useTheme();
    const ref = useRef();
    const {data: referralData} = useGetReferrals();
    const {data: prices} = useGetPrices();
    const copyRef = useRef();
    const [, setWidth] = useState(ref.current?.clientWidth);
    const [open, setOpen] = useState(false);
    const [modalSeverity, setModalSeverity] = useState('success');
    const [modalMessage, setModalMessage] = useState(null);

    const shareUrl = `${window.location.origin}/referral?code=${code}`;

    const baseTemplate = `I wanted to reach out and tell you about MM-SEAS which has made my life great. It’s so nice not to have to worry about my MMC anymore!

I’ve been given some personal referral codes from MM-SEAS that give you and me a month free! You came to mind so I wanted to make sure you got to check out MM-SEAS. Here is my personal referral link!

${shareUrl}

Let me know when you sign up!`;

    const emailBodyTemplate = encodeURIComponent(`Hi,

${baseTemplate}

MM-SEAS makes the complicated licensing process for Captains, Deckhands, Mates and Engineers simple with an easy to use software that does everything for you so you can get time back and stop worrying about getting your license.
`);

    // This is a hack to force correct auto-sizing on first render
    useEffect(() => {
        setWidth(ref.current?.clientWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current]);

    return (
        <Grid container spacing={4} justifyContent='space-around'>
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={() => setOpen(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    variant='filled'
                    severity={modalSeverity}
                    onClose={() => setOpen(false)}
                >
                    {modalMessage}
                </Alert>
            </Snackbar>
            <Typography variant='h4' gutterBottom align='center'>
                Get a <strong>FREE</strong> month for every friend you give a{' '}
                <strong>FREE</strong> month to!
            </Typography>
            <Grid item ref={ref} xs={5}>
                <QRCode
                    value={shareUrl}
                    size={ref.current?.clientWidth}
                    fgColor={theme.palette.primary.main}
                    bgColor={theme.palette.background.default}
                    level={'H'}
                    includeMargin={false}
                    imageSettings={{
                        src: '/favicon.png',
                        x: null,
                        y: null,
                        height: 24,
                        width: 24,
                        excavate: true,
                    }}
                />
            </Grid>
            <Grid
                container
                item
                xs={6}
                spacing={2}
                justifyContent='center'
                alignItems='center'
            >
                <Grid item xs={6}>
                    <SocialButton
                        icon={Facebook}
                        alt='Share your referral code on facebook'
                        url={`https://www.facebook.com/sharer/sharer.php?display=popup&u=${shareUrl}`}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SocialButton
                        icon={LinkedIn}
                        alt='Share your referral code on LinkedIn'
                        url={`http://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SocialButton
                        wide
                        icon={GMail}
                        alt='Share your referral code via email'
                        url={`mailto:?subject=${emailSubjectTemplate}&body=${emailBodyTemplate}`}
                        style={{
                            color: theme.palette.text.primary,
                            textDecoration: 'none',
                        }}
                    >
                        <Typography
                            variant='button'
                            display='block'
                            gutterBottom
                        >
                            Share via Email
                        </Typography>
                        <Typography variant='caption' display='block'>
                            (opens in mail client)
                        </Typography>
                    </SocialButton>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='h5' display='block' align='center'>
                    Your personal code:{' '}
                    <strong style={{fontFamily: 'monospace'}}>{code}</strong>
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                container
                justifyContent='center'
                alignItems='center'
                spacing={2}
            >
                <Grid item>
                    <TextField
                        label='Your personal referral link'
                        variant='outlined'
                        inputRef={copyRef}
                        value={shareUrl}
                        inputProps={{
                            style: {
                                textAlign: 'right',
                            },
                        }}
                        readOnly
                    />
                </Grid>
                <Grid item>
                    <SocialButton
                        small
                        icon={Copy}
                        alt='Copy your unique referral URL'
                        // eslint-disable-next-line no-script-url
                        onClick={e => {
                            copyRef.current.select();
                            document.execCommand('copy');
                            setModalSeverity('success');
                            setModalMessage('Copied! Go spread some joy!');
                            setOpen(true);
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        Copy Link
                    </SocialButton>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='h5' display='block' align='center'>
                    You have given {referralData?.length} referrals
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='h5' display='block' align='center'>
                    You have earned: $
                    {referralData?.length *
                        prices?.base_prices?.pro?.monthly_price || 0}{' '}
                    Credit
                </Typography>
            </Grid>
        </Grid>
    );
};

export default ReferralWidget;
export {SocialButton};
